import memoize from 'lodash/memoize'
import { Chain, createTransport, FallbackTransport, FallbackTransportConfig, Transport } from 'viem'

type Prettify<T> = { [K in keyof T]: T[K] } & {}

type OnResponseFn = (
  args: {
    method: string
    params: unknown[]
    transport: ReturnType<Transport>
  } & (
    | { error?: undefined; response: unknown; status: 'success' }
    | { error: Error; response?: undefined; status: 'error' }
  ),
) => void

type OrderArgs = { chain?: Chain; transports: readonly Transport[] }

// One-shot liveness+latency probe per chain (memoized, so it runs once). Nodes
// that answer eth_chainId are ordered fastest-first; a node that fails to answer
// is scored last. No recurring polling — the order is computed once and cached.
const getTransportOrder = memoize(
  async ({ chain, transports }: OrderArgs): Promise<number[]> => {
    if (!chain) return transports.map((_, i) => i)

    const scores = await Promise.all(
      transports.map(async (transport, i) => {
        const transport_ = transport({ chain, retryCount: 0, timeout: 1_000 })

        const start = performance.now()
        let end: number
        let success = Number.MAX_SAFE_INTEGER
        try {
          await transport_.request({ method: 'eth_chainId' })
          success = 1
        } catch {
          // unreachable node — keep MAX_SAFE_INTEGER so it sorts last
        } finally {
          end = performance.now()
        }

        return [success * (end - start), i] as const
      }),
    )

    return scores.sort((a, b) => a[0] - b[0]).map(([, i]) => i)
  },
  ({ chain }: OrderArgs) => chain?.id,
)

const rankTransports = ({
  chain,
  onTransports,
  transports,
}: {
  chain?: Chain
  onTransports: (transports: readonly Transport[]) => void
  transports: readonly Transport[]
}) => {
  getTransportOrder({ chain, transports }).then((order) => {
    onTransports(order.map((i) => transports[i]))
  })
}

// Ordered fallback (like viem's) but the initial order is set by a one-shot
// health probe instead of a recurring rank loop. Reads go to the ranked-first
// node and fail over to the next on error/timeout.
export const fallbackWithRank = <const transports extends readonly Transport[]>(
  transports_: transports,
  config: Prettify<Omit<FallbackTransportConfig, 'rank'>> = {},
): FallbackTransport => {
  const { key = 'fallback', name = 'Fallback', retryCount, retryDelay } = config
  return (({ chain, timeout, ...rest }) => {
    let transports = transports_

    let onResponse: OnResponseFn = () => {}

    const transport = createTransport(
      {
        key,
        name,
        async request({ method, params }) {
          const fetch = async (i = 0): Promise<any> => {
            const transport = transports[i]({ ...rest, chain, retryCount: 0, timeout })
            try {
              const response = await transport.request({ method, params } as any)
              onResponse({ method, params: params as unknown[], response, transport, status: 'success' })
              return response
            } catch (err) {
              onResponse({ error: err as Error, method, params: params as unknown[], transport, status: 'error' })
              // If we've reached the end of the fallbacks, throw the error.
              if (i === transports.length - 1) throw err
              // Otherwise, try the next fallback.
              return fetch(i + 1)
            }
          }
          return fetch()
        },
        retryCount,
        retryDelay,
        type: 'fallback',
      },
      {
        // eslint-disable-next-line no-return-assign
        onResponse: (fn: OnResponseFn) => (onResponse = fn),
        transports: transports.map((fn) => fn({ chain, retryCount: 0 })),
      },
    )

    rankTransports({
      chain,
      // eslint-disable-next-line no-return-assign
      onTransports: (ranked) => (transports = ranked as unknown as transports),
      transports,
    })
    return transport
  }) as FallbackTransport
}
